import { compare } from 'compare-versions'
import { FilterCondition } from 'conditions/types'
import lodash from 'lodash'
import { DynamicActionInfo } from 'types/ui/DynamicActionInfo'
import { reloadEntireApp } from 'util/misc'
import {
  FIVE_MINUTES_IN_SECONDS,
  PROGRESS_COUNTDOWN_ALERT_COMPLETE,
  PROGRESS_COUNTDOWN_ALERT_IN_PROGRESS,
  PROMO_COUNTDOWN_ROUNDING_UP,
  PROMO_ELEMENT_COUNTDOWN,
  PROMO_TYPOGRAPHY_BODY,
  PROMO_TYPOGRAPHY_TITLE,
  SECONDS_PER_MINUTE,
  TWO_HOURS_IN_SECONDS,
  WARNING_COUNTDOWN_ALERT_SEVERITY,
  WARNING_PROMO_WINDOW_2HR,
  WARNING_PROMO_WINDOW_5M,
  type WarningPromoWindow,
} from './releasePromoDialogConstants'
import {
  LiteMessage1,
  LiteMessage2,
  LiteSuccessTitle,
  ProgressCompleteMessage,
  ProgressCountdownMessage,
  ProgressCustomerTitle,
  ProgressMessage1,
  ProgressMessage2,
  ProgressMessage3,
  ProgressOvertimeMessage,
  ProgressTitle,
  ReleasePromoSuccessImageUrl,
  ReloadLabel,
  SuccessMessage1,
  SuccessMessage2,
  SuccessTitle,
  ViewReleaseNotesLabel,
  WarningCountdownMessage,
  WarningMessage1,
  WarningTitle,
} from './releasePromoDialogCopy'
import type { ProgressVariant, ReleasePromoPreviewKind, TranslateFn } from './releasePromoDialogTypes'
import { OpenSolarVersionInfo } from './useOpenSolarVersionStructs'
import {
  PromoDialog,
  PromoDialogActionRow,
  PromoDialogCountdown,
  PromoDialogElement,
  PromoDialogGap,
  PromoDialogMedia,
  PromoDialogTypography,
} from './usePromoDialogStructs'

export type { ProgressVariant, ReleasePromoPreviewKind, TranslateFn } from './releasePromoDialogTypes'

const createImageEl = (): PromoDialogMedia => ({
  type: 'image',
  media_url: ReleasePromoSuccessImageUrl,
  media_height: 300,
})

const GapSmall: PromoDialogGap = {
  type: 'gap',
  gap_size: 1,
}

const createActionRow = (actions: DynamicActionInfo[]): PromoDialogActionRow => ({
  type: 'action-row',
  actions,
})

const makeReleaseNotesAction = (href: string): DynamicActionInfo => ({
  label: ViewReleaseNotesLabel,
  href,
})

const makeReloadAction = (): DynamicActionInfo => ({
  label: ReloadLabel,
  callback: () => {
    reloadEntireApp()
  },
  color: 'primary',
})

//// Conditions — immutable templates (per-promo values use factories below)

const IsProCondition: FilterCondition = {
  path: 'auth.is_pro',
  op: 'eq',
  value: true,
}

const NotLiteCondition: FilterCondition = {
  path: 'role.user_data.is_lite',
  op: 'neq',
  value: true,
}

const LiteCondition: FilterCondition = {
  path: 'role.user_data.is_lite',
  op: 'eq',
  value: true,
}

const AccountNotBrandNewCondition: FilterCondition = {
  path: 'auth.account_age_days',
  op: 'gt',
  value: 3,
}

function createMatchVersionEq(versionSpa: string): FilterCondition {
  return {
    path: 'runtime.spa_version',
    op: 'eq',
    value: versionSpa,
  }
}

function createMatchVersionLt(versionSpa: string): FilterCondition {
  return {
    path: 'runtime.spa_version',
    op: 'lt',
    value: versionSpa,
  }
}

function createStartTimingGt(value: number): FilterCondition {
  return {
    path: 'timing.now',
    op: 'gt',
    value,
  }
}

function createEndTimingLt(value: number): FilterCondition {
  return {
    path: 'timing.now',
    op: 'lt',
    value,
  }
}

const WarningPromoMeta: Pick<PromoDialog, 'show_again_rule' | 'type' | 'priority'> = {
  show_again_rule: 'always',
  type: 'standard',
  priority: 20,
}

// Progress promo type is dynamic: 'blocker' when Staff opt in to block-during-release,
// otherwise 'standard' so the user gets a Close button on the in-progress popup.
const buildProgressPromoMeta = (
  osVersion: OpenSolarVersionInfo
): Pick<PromoDialog, 'show_again_rule' | 'type' | 'priority'> => ({
  show_again_rule: 'always',
  type: osVersion.block_user_during_release ? 'blocker' : 'standard',
  priority: 20,
})

const SuccessPromoMeta: Pick<PromoDialog, 'show_again_rule' | 'type' | 'priority'> = {
  show_again_rule: 'never',
  type: 'standard',
  priority: 20,
}

function buildSuccessConditionsList(lite: boolean, versionSpa: string): PromoDialog['conditions'] {
  const match = createMatchVersionEq(versionSpa)
  return [
    {
      conditions: lite
        ? [IsProCondition, LiteCondition, AccountNotBrandNewCondition, match]
        : [IsProCondition, NotLiteCondition, AccountNotBrandNewCondition, match],
    },
  ]
}

function buildWarningConditionsList(
  osVersion: OpenSolarVersionInfo,
  window: WarningPromoWindow
): PromoDialog['conditions'] {
  const releaseTime = new Date(osVersion.release_time).getTime() / 1000
  let startVal: number
  let endVal: number
  if (window === WARNING_PROMO_WINDOW_2HR) {
    startVal = releaseTime - TWO_HOURS_IN_SECONDS
    endVal = releaseTime - (osVersion.show_promo_warning_5m ? FIVE_MINUTES_IN_SECONDS : 0)
  } else {
    startVal = releaseTime - FIVE_MINUTES_IN_SECONDS
    endVal = releaseTime
  }
  return [
    {
      conditions: [
        createStartTimingGt(startVal),
        createEndTimingLt(endVal),
        IsProCondition,
        NotLiteCondition,
        createMatchVersionLt(osVersion.version_spa),
      ],
    },
  ]
}

function buildProgressConditionsList(osVersion: OpenSolarVersionInfo, isCustomer: boolean): PromoDialog['conditions'] {
  const releaseTime = new Date(osVersion.release_time).getTime() / 1000
  return [
    {
      conditions: [
        createStartTimingGt(releaseTime),
        { ...IsProCondition, value: !isCustomer },
        createMatchVersionLt(osVersion.version_spa),
      ],
    },
  ]
}

function buildSuccessElements(
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn,
  lite: boolean
): PromoDialogElement[] {
  const subs = { newVersion: osVersion.version }
  const highlights = osVersion.release_highlights || []
  const releaseNotesAction = makeReleaseNotesAction(osVersion.release_notes_url || '')

  const titleText = translate(lite ? LiteSuccessTitle : SuccessTitle, subs)
  const body1Text = lite ? LiteMessage1 : SuccessMessage1
  const body2Text = lite ? LiteMessage2 : SuccessMessage2

  const TitleEl: PromoDialogTypography = { type: PROMO_TYPOGRAPHY_TITLE, text: titleText }
  const BodyEl1: PromoDialogTypography = { type: PROMO_TYPOGRAPHY_BODY, text: body1Text }
  const BodyEl2: PromoDialogTypography = { type: PROMO_TYPOGRAPHY_BODY, text: body2Text }

  const extra = lite ? osVersion.extra_content_success_lite || [] : osVersion.extra_content_success || []
  const hideDefault = lite ? osVersion.hide_def_content_success_lite : osVersion.hide_def_content_success

  if (hideDefault) {
    return [...highlights, ...extra]
  }
  if (osVersion.release_notes_url) {
    BodyEl2.text = body2Text
    return [TitleEl, createImageEl(), BodyEl1, BodyEl2, ...highlights, ...extra, createActionRow([releaseNotesAction])]
  }
  return [TitleEl, createImageEl(), BodyEl1, ...highlights, ...extra]
}

function buildSuccessPromo(osVersion: OpenSolarVersionInfo, translate: TranslateFn, lite: boolean): PromoDialog {
  return {
    ...SuccessPromoMeta,
    conditions: buildSuccessConditionsList(lite, osVersion.version_spa),
    elements: buildSuccessElements(osVersion, translate, lite),
  }
}

function buildWarningElements(
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn,
  window: WarningPromoWindow
): PromoDialogElement[] {
  const subs = { newVersion: osVersion.version }
  const highlights = osVersion.release_highlights || []
  const releaseTime = new Date(osVersion.release_time).getTime() / 1000

  const TitleEl: PromoDialogTypography = { type: PROMO_TYPOGRAPHY_TITLE, text: translate(WarningTitle, subs) }
  const BodyEl1: PromoDialogTypography = { type: PROMO_TYPOGRAPHY_BODY, text: WarningMessage1 }
  const ReleaseCountdown: PromoDialogCountdown = {
    type: PROMO_ELEMENT_COUNTDOWN,
    alert_severity: WARNING_COUNTDOWN_ALERT_SEVERITY,
    countdown_time: releaseTime,
    countdown_msg: WarningCountdownMessage,
    countdown_overtime_msg: WarningCountdownMessage,
    rounding: PROMO_COUNTDOWN_ROUNDING_UP,
  }

  const extra =
    window === WARNING_PROMO_WINDOW_2HR
      ? osVersion.extra_content_warning_2h || []
      : osVersion.extra_content_warning_5m || []
  const hideDefault =
    window === WARNING_PROMO_WINDOW_2HR ? osVersion.hide_def_content_warning_2hr : osVersion.hide_def_content_warning_5m

  if (hideDefault) {
    return [...highlights, ...extra]
  }
  return [TitleEl, createImageEl(), BodyEl1, ...highlights, ...extra, ReleaseCountdown]
}

function buildWarningPromo(
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn,
  window: WarningPromoWindow
): PromoDialog {
  return {
    ...WarningPromoMeta,
    conditions: buildWarningConditionsList(osVersion, window),
    elements: buildWarningElements(osVersion, translate, window),
  }
}

function buildProgressElements(
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn,
  variant: ProgressVariant
): PromoDialogElement[] {
  const subs = { newVersion: osVersion.version }
  const highlights = osVersion.release_highlights || []
  const extra = osVersion.extra_content_progress || []
  const releaseTime = new Date(osVersion.release_time).getTime() / 1000
  const releaseNotesAction = makeReleaseNotesAction(osVersion.release_notes_url || '')
  const reloadAction = makeReloadAction()

  const isCustomer = variant === 'customer_incomplete' || variant === 'customer_complete'
  const isComplete = variant === 'pro_complete' || variant === 'customer_complete'

  const TitleEl: PromoDialogTypography = {
    type: PROMO_TYPOGRAPHY_TITLE,
    text: isCustomer ? ProgressCustomerTitle : translate(ProgressTitle, subs),
  }
  const BodyEl1: PromoDialogTypography = { type: PROMO_TYPOGRAPHY_BODY, text: ProgressMessage1 }
  const BodyEl2: PromoDialogTypography = {
    type: PROMO_TYPOGRAPHY_BODY,
    text: osVersion.release_notes_url ? ProgressMessage2 : ProgressMessage3,
  }

  const ReleaseCountdown: PromoDialogCountdown = {
    type: PROMO_ELEMENT_COUNTDOWN,
    alert_severity: PROGRESS_COUNTDOWN_ALERT_IN_PROGRESS,
    countdown_time: releaseTime + osVersion.release_duration_mins * SECONDS_PER_MINUTE,
    countdown_msg: ProgressCountdownMessage,
    countdown_overtime_msg: ProgressOvertimeMessage,
    rounding: PROMO_COUNTDOWN_ROUNDING_UP,
  }

  if (isComplete) {
    ReleaseCountdown.alert_severity = PROGRESS_COUNTDOWN_ALERT_COMPLETE
    ReleaseCountdown.countdown_msg = ProgressCompleteMessage
    ReleaseCountdown.countdown_overtime_msg = ProgressCompleteMessage
  }

  if (osVersion.hide_def_content_progress) {
    if (isCustomer && !isComplete) {
      return [...extra]
    }
    return [...highlights, ...extra]
  }

  if (isComplete) {
    const includedActions = osVersion.release_notes_url ? [releaseNotesAction, reloadAction] : [reloadAction]
    return [
      TitleEl,
      createImageEl(),
      BodyEl1,
      ...highlights,
      ...extra,
      ReleaseCountdown,
      createActionRow(includedActions),
    ]
  }

  if (isCustomer) {
    return [TitleEl, createImageEl(), BodyEl1, ...extra, ReleaseCountdown, GapSmall]
  }

  // Pro, update in progress: body copy references release notes when URL is set — include the same action row as complete (without Reload until done)
  if (osVersion.release_notes_url) {
    return [
      TitleEl,
      createImageEl(),
      BodyEl1,
      ...highlights,
      ...extra,
      ReleaseCountdown,
      BodyEl2,
      createActionRow([releaseNotesAction]),
      GapSmall,
    ]
  }

  return [TitleEl, createImageEl(), BodyEl1, ...highlights, ...extra, ReleaseCountdown, BodyEl2, GapSmall]
}

function buildProgressPromo(
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn,
  variant: ProgressVariant
): PromoDialog {
  const isCustomer = variant === 'customer_incomplete' || variant === 'customer_complete'
  return {
    ...buildProgressPromoMeta(osVersion),
    conditions: buildProgressConditionsList(osVersion, isCustomer),
    elements: buildProgressElements(osVersion, translate, variant),
  }
}

const baseKeys = (structKey: string, version: string) => {
  const baseKey = `release-promo-${structKey}-${version}-`
  return {
    key_warning2hr: `${baseKey}warning-2hr`,
    key_warning5m: `${baseKey}warning-5m`,
    key_progress: `${baseKey}progress`,
    key_progress_customer: `${baseKey}progress-customer`,
    key_success: `${baseKey}success`,
    key_success_lite: `${baseKey}success-lite`,
  }
}

/**
 * Computes promo-dialog structs to add/remove for one opensolar-version struct (matches useReleasePromos behaviour).
 */
export function computeReleasePromosForVersionStruct(
  structKey: string,
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn,
  currentSpaVersion: string
): { add: Array<{ key: string; data: PromoDialog }>; removeKeys: string[] } {
  const keys = baseKeys(structKey, osVersion.version)
  const removeKeys: string[] = [
    keys.key_warning2hr,
    keys.key_warning5m,
    keys.key_progress,
    keys.key_progress_customer,
    keys.key_success,
    keys.key_success_lite,
  ]

  const add: Array<{ key: string; data: PromoDialog }> = []

  if (compare(currentSpaVersion, osVersion.version_spa, '>')) {
    return { add: [], removeKeys }
  }

  if (osVersion.show_promo_success) {
    add.push({ key: keys.key_success, data: buildSuccessPromo(osVersion, translate, false) })
  }

  if (osVersion.show_promo_success_lite) {
    add.push({ key: keys.key_success_lite, data: buildSuccessPromo(osVersion, translate, true) })
  }

  if (compare(currentSpaVersion, osVersion.version_spa, '=')) {
    return { add, removeKeys: removeKeys.filter((k) => !add.find((a) => a.key === k)) }
  }

  if (osVersion.show_promo_warning_2hr && !osVersion.release_complete) {
    add.push({ key: keys.key_warning2hr, data: buildWarningPromo(osVersion, translate, WARNING_PROMO_WINDOW_2HR) })
  }

  if (osVersion.show_promo_warning_5m && !osVersion.release_complete) {
    add.push({ key: keys.key_warning5m, data: buildWarningPromo(osVersion, translate, WARNING_PROMO_WINDOW_5M) })
  }

  if (osVersion.show_promo_progress) {
    const complete = osVersion.release_complete
    add.push({
      key: keys.key_progress,
      data: buildProgressPromo(osVersion, translate, complete ? 'pro_complete' : 'pro_incomplete'),
    })
    add.push({
      key: keys.key_progress_customer,
      data: buildProgressPromo(osVersion, translate, complete ? 'customer_complete' : 'customer_incomplete'),
    })
  }

  const toRemove = removeKeys.filter((k) => !add.find((a) => a.key === k))
  return { add, removeKeys: toRemove }
}

export function buildReleasePromoPreview(
  kind: ReleasePromoPreviewKind,
  osVersion: OpenSolarVersionInfo,
  translate: TranslateFn
): { dialogType: 'standard' | 'blocker'; elements: PromoDialogElement[] } {
  const progressDialogType: 'standard' | 'blocker' = osVersion.block_user_during_release ? 'blocker' : 'standard'

  const map: Record<
    ReleasePromoPreviewKind,
    () => { dialogType: 'standard' | 'blocker'; elements: PromoDialogElement[] }
  > = {
    warning_2hr: () => ({
      dialogType: 'standard',
      elements: buildWarningElements(osVersion, translate, WARNING_PROMO_WINDOW_2HR),
    }),
    warning_5m: () => ({
      dialogType: 'standard',
      elements: buildWarningElements(osVersion, translate, WARNING_PROMO_WINDOW_5M),
    }),
    progress_in_progress_pro: () => ({
      dialogType: progressDialogType,
      elements: buildProgressElements(osVersion, translate, 'pro_incomplete'),
    }),
    progress_complete_pro: () => ({
      dialogType: progressDialogType,
      elements: buildProgressElements(osVersion, translate, 'pro_complete'),
    }),
    progress_in_progress_customer: () => ({
      dialogType: progressDialogType,
      elements: buildProgressElements(osVersion, translate, 'customer_incomplete'),
    }),
    progress_complete_customer: () => ({
      dialogType: progressDialogType,
      elements: buildProgressElements(osVersion, translate, 'customer_complete'),
    }),
    success: () => ({
      dialogType: 'standard',
      elements: buildSuccessElements(osVersion, translate, false),
    }),
    success_lite: () => ({
      dialogType: 'standard',
      elements: buildSuccessElements(osVersion, translate, true),
    }),
  }

  return map[kind]()
}

/** Replace action-row callbacks for safe preview in Staff UI (avoid full app reload). `href` links are unchanged. */
export function sanitizePromoElementsForPreview(elements: PromoDialogElement[]): PromoDialogElement[] {
  return elements.map((el) => {
    if (el.type === 'action-row') {
      const copy = lodash.cloneDeep(el)
      copy.actions = (copy.actions ?? []).map((a) => ({
        ...a,
        callback: a.callback ? () => {} : undefined,
      }))
      return copy
    }
    return el
  })
}
